import React, { useEffect, useState, useMemo } from 'react';
import { Observer } from 'mobx-react';
import {
  Button,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@material-ui/core';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import { toJS } from 'mobx';
import { useHistory } from 'react-router-dom';
import { Formik } from 'formik';
import { css } from '@emotion/core';
import { safeFalsy, safeEval } from '../util/UtilFunctions';
import { useRootStore } from '../../stores/BridgeProvider';
import Spinner from '../util/Spinner';
import AccountSelect from '../util/AccountSelect';
import { FlexDiv, WideBox, StyledForm } from '../styles/styles';

const TranCatForm = ({ onSubmit, transactionCategory = {}, deepLink }) => {
  return (
    <Observer
      render={() => {
        const { dataStore } = useRootStore();
        const history = useHistory();
        const [isLoading, setLoading] = useState(true);
        const accounts = dataStore.accountArray;

        const sortedAccounts = useMemo(
          () =>
            accounts.slice().sort((a, b) => {
              return a.AccountName.localeCompare(b.AccountName);
            }),
          [accounts],
        );
        useEffect(() => {
          const initialActions = async () => {
            const requests = [];
            if (accounts.length === 0) {
              requests.push(dataStore.retrieveAccounts());
            }
            await Promise.all(requests);
            setLoading(false);
          };
          initialActions();
        }, []);
        if (isLoading || !transactionCategory) return <Spinner />;
        return (
          <Formik
            initialValues={{
              transactionCategoryId: safeFalsy(
                transactionCategory.TranCategoryId,
              ),
              code: safeFalsy(transactionCategory.Code),
              name: safeFalsy(transactionCategory.Name),
              apAccountId: safeFalsy(transactionCategory.APAccountId),
              apAccountObject: dataStore.getAccountFromId(
                transactionCategory.APAccountId,
              ),
              arAccountId: safeFalsy(transactionCategory.ARAccountId),
              arAccountObject: dataStore.getAccountFromId(
                transactionCategory.ARAccountId,
              ),
              apContraAccountId: safeFalsy(
                transactionCategory.APContraAccountId,
              ),
              apContraAccountObject: dataStore.getAccountFromId(
                transactionCategory.APContraAccountId,
              ),
              arContraAccountId: safeFalsy(
                transactionCategory.ARContraAccountId,
              ),
              arContraAccountObject: dataStore.getAccountFromId(
                transactionCategory.ARContraAccountId,
              ),
              statusId: safeFalsy(transactionCategory.StatusId, 1),
            }}
            onSubmit={async (values, actions) => {
              try {
                await onSubmit(values);
              } catch (e) {
                console.error(e);
              }
              actions.setSubmitting(false);
            }}
            render={({
              handleBlur,
              handleChange,
              setFieldValue,
              errors,
              handleSubmit,
              values,
              isSubmitting,
              dirty,
            }) => {
              const moveAccountTypeToFront = (list, typeId) => {
                const index = list.findIndex(({ id }) => id === typeId);
                list.splice(0, 0, list.splice(index, 1)[0]);
                return list;
              };
              return (
                <div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '8px',
                    }}
                  >
                    <div style={{ margin: '0 auto' }}>
                      <Typography variant="h2" gutterBottom>
                        {transactionCategory.TranCategoryId
                          ? 'Edit Transaction Category'
                          : 'New Transaction Category'}
                      </Typography>
                    </div>
                    {transactionCategory.TranCategoryId && deepLink !== '#' && (
                      <Tooltip title="View Item In Xero" placement="left" arrow>
                        <a
                          style={{ color: '#263238', textDecoration: 'none' }}
                          href={deepLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExitToAppIcon />
                        </a>
                      </Tooltip>
                    )}
                  </div>
                  <StyledForm onSubmit={handleSubmit}>
                    <TextField
                      name="name"
                      label="Category Name"
                      value={values.name}
                      margin="normal"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      variant="outlined"
                      fullWidth
                      error={errors.name}
                    />
                    <TextField
                      name="code"
                      label="Category Code"
                      value={values.code}
                      margin="normal"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      variant="outlined"
                      fullWidth
                      error={errors.code}
                    />
                    <div style={{ width: '100%', zIndex: 1004 }}>
                      <AccountSelect
                        placeholder="AP Account"
                        value={values.apAccountObject}
                        isValidAccountGroup={TypeId => [2].includes(TypeId)}
                        onChange={val => {
                          setFieldValue('apAccountObject', val);
                          setFieldValue(
                            'apAccountId',
                            safeEval(() => val.AccountId),
                            null,
                          );
                        }}
                      />
                    </div>
                    <div style={{ width: '100%', zIndex: 1003 }}>
                      <AccountSelect
                        placeholder="AR Account"
                        value={values.arAccountObject}
                        isValidAccountGroup={TypeId => [1].includes(TypeId)}
                        onChange={val => {
                          setFieldValue('arAccountObject', val);
                          setFieldValue(
                            'arAccountId',
                            safeEval(() => val.AccountId),
                            null,
                          );
                        }}
                      />
                    </div>
                    <div style={{ width: '100%', zIndex: 1002 }}>
                      <AccountSelect
                        placeholder="AP Contra Account"
                        value={values.apContraAccountObject}
                        isValidAccountGroup={TypeId => ![1, 2].includes(TypeId)}
                        sortFunction={list => moveAccountTypeToFront(list, 7)}
                        onChange={val => {
                          setFieldValue('apContraAccountObject', val);
                          setFieldValue(
                            'apContraAccountId',
                            safeEval(() => val.AccountId),
                            null,
                          );
                        }}
                      />
                    </div>
                    <div style={{ width: '100%', zIndex: 1001 }}>
                      <AccountSelect
                        placeholder="AR Contra Account"
                        value={values.arContraAccountObject}
                        isValidAccountGroup={TypeId => ![1, 2].includes(TypeId)}
                        sortFunction={list => moveAccountTypeToFront(list, 6)}
                        onChange={val => {
                          setFieldValue('arContraAccountObject', val);
                          setFieldValue(
                            'arContraAccountId',
                            safeEval(() => val.AccountId),
                            null,
                          );
                        }}
                      />
                    </div>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!values.statusId}
                          onChange={(e, val) => {
                            setFieldValue('statusId', val);
                          }}
                          value={values.statusId}
                        />
                      }
                      label="Active"
                    />
                    <div
                      css={css`
                        display: flex;
                        margin-top: 1.2rem;
                        justify-content: flex-end;
                        & > button,
                        a {
                          margin: 5px;
                        }
                      `}
                    >
                      <Button
                        color="primary"
                        variant="contained"
                        type="submit"
                        size="large"
                        disabled={!dirty || isSubmitting}
                      >
                        Submit
                      </Button>
                      <Button
                        color="secondary"
                        variant="outlined"
                        size="large"
                        onClick={() => history.goBack()}
                      >
                        Cancel
                      </Button>
                    </div>
                  </StyledForm>
                </div>
              );
            }}
          />
        );
      }}
    />
  );
};

export default TranCatForm;
